import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Robot, Lightning, EnvelopeSimple, Users, ChatCircleDots, ArrowRight,
  CheckCircle, Sparkle, Clock, Target, Plugs, ShieldCheck, ArrowUpRight,
  CaretRight,
} from "@phosphor-icons/react";

const fade = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: .5 } };

export default function Landing() {
  return (
    <div className="bg-white text-[var(--ink)]">
      {/* Top nav */}
      <header className="border-b border-[var(--line)] sticky top-0 bg-white/90 backdrop-blur z-40">
        <div className="max-w-[1320px] mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 bg-[var(--ink)] flex items-center justify-center">
              <Robot size={22} weight="duotone" color="#FFD600" />
            </div>
            <span className="font-display text-2xl tracking-tight">Robocope</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#how" className="hover:underline">How it works</a>
            <a href="#automations" className="hover:underline">Automations</a>
            <Link to="/pricing" className="hover:underline" data-testid="nav-pricing">Pricing</Link>
            <a href="#faq" className="hover:underline">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium hover:underline" data-testid="nav-login">Log in</Link>
            <Link to="/register" className="btn-primary text-sm" data-testid="nav-signup">
              Hire your AI employee <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="max-w-[1320px] mx-auto px-8 pt-20 pb-24 grid lg:grid-cols-12 gap-12 relative">
          <motion.div {...fade} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[var(--ink)] px-3 py-1.5 mb-8">
              <span className="w-2 h-2 bg-[var(--brand)] rounded-full animate-pulse" />
              <span className="overline">V1 · Now hiring small businesses</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-[78px] font-display leading-[0.96] tracking-tighter">
              You just hired<br />
              <span className="bg-[var(--accent)] px-3 inline-block -rotate-1 mt-2">an AI employee.</span>
            </h1>
            <p className="text-xl text-[var(--ink-soft)] mt-8 max-w-[560px] leading-relaxed">
              Robocope handles your daily workload — emails, leads and customer chats — so your team can focus on what actually moves the business.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-primary">
                Start free <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/pricing" className="btn-secondary" data-testid="hero-cta-secondary">
                See pricing <CaretRight size={18} weight="bold" />
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[var(--ink-soft)]">
              <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand)]" /> No credit card</div>
              <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand)]" /> 14-day trial</div>
              <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand)]" /> Cancel anytime</div>
            </div>
          </motion.div>

          <motion.div {...fade} transition={{ duration: .6, delay: .15 }} className="lg:col-span-5 relative">
            {/* Bento preview */}
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-6 cell-dark p-5">
                <div className="overline text-white/60">Inbox · just now</div>
                <div className="mt-2 text-sm text-white/70 font-mono-rc">From: alex.morgan@brightcorp.com</div>
                <div className="text-white text-base mt-1">Interested in a custom quote</div>
                <div className="mt-4 flex items-center gap-2">
                  <span className="tag tag-yellow">LEAD</span>
                  <span className="tag" style={{color:'#fff'}}>Priority: HIGH</span>
                </div>
                <div className="mt-4 text-xs text-white/60">Draft reply ready in 1.2s</div>
              </div>
              <div className="col-span-3 cell-strong p-5 bg-white">
                <Users size={28} weight="duotone" className="text-[var(--brand)]" />
                <div className="mt-3 text-3xl font-display">1,284</div>
                <div className="text-xs text-[var(--ink-soft)] mt-1">Leads scored this week</div>
              </div>
              <div className="col-span-3 cell-strong p-5" style={{background:'var(--accent)'}}>
                <Lightning size={28} weight="fill" />
                <div className="mt-3 text-3xl font-display">−87%</div>
                <div className="text-xs mt-1">Time on email</div>
              </div>
              <div className="col-span-6 cell-strong p-5 bg-white">
                <div className="flex items-center justify-between">
                  <div className="overline text-[var(--ink-soft)]">Live · Customer Bot</div>
                  <div className="text-xs text-[var(--good)] font-mono-rc">● ONLINE</div>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="bg-[var(--bg-soft)] p-2 inline-block">Are you open Sundays?</div>
                  <div className="bg-[var(--brand)] text-white p-2 inline-block ml-auto block w-fit">We're open Mon–Sat, 9–6. Want me to book you a slot?</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-4 w-24 h-24 bg-[var(--accent)] -rotate-6 hidden lg:block" />
          </motion.div>
        </div>
      </section>

      {/* Outcome strip */}
      <section className="border-y border-[var(--ink)] bg-[var(--ink)] text-white py-6 overflow-hidden">
        <div className="marquee">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-16 font-display text-2xl tracking-tight">
              <span>LESS MANUAL WORK</span>
              <span className="text-[var(--accent)]">●</span>
              <span>FASTER RESPONSE TIMES</span>
              <span className="text-[var(--accent)]">●</span>
              <span>FEWER MISTAKES</span>
              <span className="text-[var(--accent)]">●</span>
              <span>NO SETUP HEADACHE</span>
              <span className="text-[var(--accent)]">●</span>
            </div>
          ))}
        </div>
      </section>

      {/* The 3 automations */}
      <section id="automations" className="py-24">
        <div className="max-w-[1320px] mx-auto px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <div className="overline text-[var(--ink-soft)]">/ The work</div>
              <h2 className="text-4xl lg:text-5xl font-display tracking-tight mt-3">
                Three jobs your AI employee already knows how to do.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg text-[var(--ink-soft)] leading-relaxed">
                Drop in. Plug in your inbox, your leads spreadsheet, and your website. Robocope reads, sorts, drafts, scores and replies — quietly, all day.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <FeatureCard
              span="lg:col-span-7"
              icon={EnvelopeSimple}
              n="01"
              title="Email Automation"
              copy="Auto-reads incoming emails. Tags them as lead, support or spam. Drafts the reply in your voice."
              bullets={["Categorize & prioritize inbox", "Draft replies in your tone", "Surface only what needs you"]}
              dark
            />
            <FeatureCard
              span="lg:col-span-5"
              icon={Users}
              n="02"
              title="Lead Processing"
              copy="Cleans messy lead data, deduplicates, scores quality, and routes to the right buyer."
              bullets={["CSV upload or web form", "Good · risky · bad scoring", "Auto-route to sales"]}
              accent
            />
            <FeatureCard
              span="lg:col-span-12"
              icon={ChatCircleDots}
              n="03"
              title="Customer Response Bot"
              copy="A chat widget for your site that answers FAQs, captures leads, and hands off when it can't."
              bullets={["Embeddable script tag", "Train on your FAQs", "Lead capture built-in"]}
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[var(--bg-soft)] py-24 border-y border-[var(--line)]">
        <div className="max-w-[1320px] mx-auto px-8">
          <div className="overline text-[var(--ink-soft)]">/ Onboarding the new hire</div>
          <h2 className="text-4xl lg:text-5xl font-display tracking-tight mt-3 mb-14 max-w-3xl">
            From signup to first reply in under five minutes.
          </h2>
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Sign in", d: "Email or Google. Workspace ready instantly." },
              { n: "02", t: "Show it the work", d: "Forward emails, upload leads, paste your FAQs. Done in minutes." },
              { n: "03", t: "Approve and ship", d: "Review the first batch of drafts and scores. Hit go. It runs forever." },
            ].map((s) => (
              <div key={s.n} className="cell-strong bg-white p-8">
                <div className="font-display text-6xl text-[var(--brand)]">{s.n}</div>
                <div className="font-display text-2xl mt-4">{s.t}</div>
                <div className="text-[var(--ink-soft)] mt-2">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-24">
        <div className="max-w-[1320px] mx-auto px-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="overline text-[var(--ink-soft)]">/ Why it works</div>
            <h2 className="text-4xl lg:text-5xl font-display tracking-tight mt-3 mb-6">
              Don't sell AI. Sell back the hours.
            </h2>
            <p className="text-lg text-[var(--ink-soft)] leading-relaxed">
              Most AI tools ask you to learn a new app. Robocope just does the work. Like a smart, fast new hire that never sleeps.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { i: Clock, t: "−87% time on email", d: "From 2 hours to 15 minutes a day." },
              { i: Target, t: "3.4× lead conversion", d: "Better routing means buyers get the right prospects." },
              { i: ShieldCheck, t: "0 missed replies", d: "Every email gets a draft. Nothing falls through." },
              { i: Plugs, t: "No-setup integrations", d: "Pre-wired for inbox, CSV and website." },
            ].map((b) => (
              <div key={b.t} className="cell-strong p-6">
                <b.i size={28} weight="duotone" className="text-[var(--brand)]" />
                <div className="font-display text-xl mt-3">{b.t}</div>
                <div className="text-sm text-[var(--ink-soft)] mt-1">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-[var(--ink)] text-white py-20">
        <div className="max-w-[1100px] mx-auto px-8">
          <Sparkle size={32} weight="fill" className="text-[var(--accent)]" />
          <p className="text-3xl lg:text-4xl font-display tracking-tight mt-6 leading-tight">
            "We don't say 'we use AI.' We say 'we hired Robocope.' Felt like adding a fourth person to a three-person team — overnight."
          </p>
          <div className="mt-8 flex items-center gap-4">
            <img
              src="https://images.pexels.com/photos/36729739/pexels-photo-36729739.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=120"
              alt="Customer"
              className="w-14 h-14 object-cover"
            />
            <div>
              <div className="font-medium">Jasmine Park</div>
              <div className="text-sm text-white/60">Founder, Park Studio · 30 staff</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24">
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="overline text-[var(--ink-soft)]">/ Common questions</div>
          <h2 className="text-4xl lg:text-5xl font-display tracking-tight mt-3 mb-12">Honest answers.</h2>
          <div className="space-y-4">
            {[
              { q: "Is this just ChatGPT in a wrapper?", a: "No. Robocope is workflows: email triage, lead scoring, customer chat — running on Claude with your business context, your tone, and your FAQs." },
              { q: "Will it reply to emails without me?", a: "Only if you turn that on. By default, Robocope drafts and you approve in one click. You're always in control." },
              { q: "What kind of business is this for?", a: "Small to mid teams: agencies, real estate, e-commerce, local service businesses — anyone drowning in inbound." },
              { q: "Can I cancel anytime?", a: "Yes. Month-to-month, no contracts. We'd rather earn the renewal." },
            ].map((f, i) => (
              <details key={i} className="cell-strong bg-white p-6 group" data-testid={`faq-${i}`}>
                <summary className="font-display text-xl cursor-pointer flex items-center justify-between">
                  {f.q}
                  <ArrowUpRight size={22} weight="bold" className="group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-[var(--ink-soft)] mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--accent)] border-y border-[var(--ink)]">
        <div className="max-w-[1320px] mx-auto px-8 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h2 className="text-5xl lg:text-7xl font-display tracking-tighter leading-[0.95]">
              Stop doing the work a new hire would do.
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col items-start gap-4">
            <p className="text-lg">Spin up your AI employee in 60 seconds. 14 days free.</p>
            <Link to="/register" className="btn-primary text-base" data-testid="footer-cta">
              Start hiring <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[var(--ink)] text-white py-10">
        <div className="max-w-[1320px] mx-auto px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[var(--accent)] flex items-center justify-center"><Robot size={16} weight="duotone" color="#0A0A0A" /></div>
            <span className="font-display text-lg">Robocope</span>
            <span className="text-white/50 text-xs ml-3">© 2026 Robocope. All rights reserved.</span>
          </div>
          <div className="flex gap-6 text-sm text-white/60">
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <Link to="/login" className="hover:text-white">Log in</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <a href="mailto:hello@robocope.com" className="hover:text-white">hello@robocope.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ span = "", icon: Icon, n, title, copy, bullets, dark, accent }) {
  const cls = dark
    ? "cell-strong bg-[var(--ink)] text-white"
    : accent
    ? "cell-strong"
    : "cell-strong bg-white";
  const bg = accent ? { background: "var(--accent)" } : {};
  return (
    <div className={`${span} ${cls} p-8`} style={bg}>
      <div className="flex items-start justify-between gap-4">
        <div className={`${dark ? "text-white" : ""}`}>
          <div className={`overline ${dark ? "text-white/50" : "text-[var(--ink-soft)]"}`}>/ {n}</div>
          <div className="font-display text-3xl tracking-tight mt-2">{title}</div>
        </div>
        <div className={`w-12 h-12 flex items-center justify-center ${dark ? "bg-[var(--accent)]" : "bg-[var(--ink)]"}`}>
          <Icon size={24} weight="duotone" color={dark ? "#0A0A0A" : "#FFD600"} />
        </div>
      </div>
      <p className={`mt-4 ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{copy}</p>
      <ul className="mt-6 space-y-2">
        {bullets.map((b) => (
          <li key={b} className="flex items-center gap-2 text-sm">
            <CheckCircle size={18} weight="fill" className={dark ? "text-[var(--accent)]" : "text-[var(--brand)]"} />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}
